import {
  ENUM_LABELS,
  type ImportRowView,
  type ImportRunView,
  importRowStatusSchema,
} from '#shared/types';
import { Button } from '#client/components/ui/Button';
import { Card } from '#client/components/ui/Card';
import { DataTable } from '#client/components/ui/DataTable';
import { EmptyState } from '#client/components/ui/EmptyState';
import { Pagination } from '#client/components/ui/Pagination';
import { Segmented } from '#client/components/ui/Segmented';
import { importUrls } from '#client/lib/endpoints';
import { CHECK_COLUMNS } from './check-columns';
import type { UseResourceListResult } from '#client/hooks/useResourceList';

const STATUS_FILTERS = [
  { value: '', label: 'All' },
  ...importRowStatusSchema.options
    .filter((option) => option !== 'pending' && option !== 'committed')
    .map((option) => ({
      value: option,
      label: ENUM_LABELS.importRowStatus[option],
    })),
];

/** Every staged row, filterable by what the check made of it. */
export function CheckRowsCard({
  run,
  rows,
  status,
  isChecking,
  onStatusChange,
  onCheck,
}: {
  run: ImportRunView;
  rows: UseResourceListResult<ImportRowView>;
  status: string;
  isChecking: boolean;
  onStatusChange: (status: string) => void;
  onCheck: () => void;
}) {
  return (
    <Card
      title="Every row"
      subtitle={`${run.ok_rows} ready · ${run.warning_rows} with a warning · ${run.error_rows} needing attention · ${run.skipped_rows} skipped`}
      actions={
        <div className="c-card__actions">
          <Button size="sm" onClick={onCheck} loading={isChecking}>
            Check again
          </Button>
          {run.error_rows > 0 && (
            <a
              className="c-button c-button--secondary c-button--sm"
              href={importUrls.errors(run.id)}
            >
              Download the rows to fix
            </a>
          )}
        </div>
      }
      flush
    >
      <div className="c-import-filter">
        <Segmented label="Show" options={STATUS_FILTERS} value={status} onChange={onStatusChange} />
      </div>

      <DataTable
        columns={CHECK_COLUMNS}
        rows={rows.items}
        rowKey={(row) => row.id}
        loading={rows.loading}
        skeletonRows={rows.limit}
        caption="Rows in this import"
        emptyState={<EmptyState title="No rows match this filter" />}
      />

      <Pagination
        page={rows.page}
        limit={rows.limit}
        count={rows.count}
        onPageChange={rows.setPage}
      />
    </Card>
  );
}
